import React, { useState, useCallback } from "react";
import { styled } from "@mui/material/styles";

interface ToggleTabOption {
  value: string;
  label: string;
}

interface ToggleTabProps {
  value?: string;
  onChange?: (value: string) => void;
  options: ToggleTabOption[];
  className?: string;
  storageKey?: string; // Key for localStorage persistence
  defaultValue?: string; // Default value if no stored value exists
}

// Custom hook to manage tab state with localStorage and video generation redirect
export const useToggleTabState = ({
  options,
  storageKey,
  defaultValue,
}: {
  storageKey: string;
  defaultValue: string;
  options:ToggleTabOption[];
}) => {
  const [activeTab, setActiveTab] = useState<string>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored && options.some(option => option.value === stored)) {
        return stored;
      }
    } catch (error) {
      console.warn('Failed to read from localStorage:', error);
    }
    return defaultValue;
  });

  const handleTabChange = useCallback((newValue: string) => {
    setActiveTab(newValue);
    try {
      localStorage.setItem(storageKey, newValue);
    } catch (error) {
      console.warn('Failed to save to localStorage:', error);
    }
  }, [storageKey]);

  // Function to redirect to "All" tab when video is generated
  const redirectToAllTab = useCallback(() => {
    const allTab = options.find(option => 
      option.value.toLowerCase() === 'all' || 
      option.label.toLowerCase() === 'all'
    );
    
    if (allTab) {
      handleTabChange(allTab.value);
    }
  }, [options, handleTabChange]);

  return {
    activeTab,
    handleTabChange,
    redirectToAllTab
  };
};

const TabContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
});

const TabButton = styled('button')<{ isActive: boolean }>(({ isActive }) => ({
  background: 'none',
  border: 'none',
  padding: '12px 16px',
  fontFamily: 'Maven Pro, Inter, sans-serif',
  fontSize: '16px',
  fontWeight: isActive ? 600 : 400,
  color: isActive ? '#F99A7B' : '#9CA3AF',
  cursor: 'pointer',
  position: 'relative',
  transition: 'color 0.2s ease',
  outline: 'none',
  
  '&:hover': {
    color: isActive ? '#F99A7B' : '#6B7280',
  },
  
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-1px',
    left: 0,
    right: 0,
    height: '2px',
    background: isActive ? 'linear-gradient(90deg, #929BED, #F99A7B)' : 'transparent',
    transition: 'background 0.2s ease',
  },
}));

export const ToggleTab: React.FC<ToggleTabProps> = ({ 
  value: controlledValue, 
  onChange: controlledOnChange,
  options,
  className,
  storageKey = 'picasso-toggle-tab',
  defaultValue = 'All'
}) => {
  // Use internal state management when no external control is provided
  const { activeTab: internalValue, handleTabChange: internalOnChange } = useToggleTabState({
    options,
    storageKey,
    defaultValue
  });

  // Determine whether to use controlled or uncontrolled behavior
  const isControlled = controlledValue !== undefined && controlledOnChange !== undefined;
  const currentValue = isControlled ? controlledValue : internalValue;
  const handleChange = isControlled ? controlledOnChange : internalOnChange;

  return (
    <TabContainer className={className}>
      {options.map((option) => (
        <TabButton
          key={option.value}
          isActive={currentValue === option.value}
          onClick={() => handleChange(option.value)}
          type="button"
        >
          {option.label}
        </TabButton>
      ))}
    </TabContainer>
  );
};

export default ToggleTab;
